/* eslint-disable vue/no-side-effects-in-computed-properties */
import CheckinDropdown from '@/components/reservations/reservationLists/CheckinDropdown.vue'
import { mapGetters, mapActions } from 'vuex'
import { eventHub } from '@/main'

import _ from 'lodash'
import moment from 'moment'
import iconsColumn from '../components/reservations/reservationLists/columns/icons'
import { RESERVATION_STATUS } from '@/enums/reservationStatus'
import { PAYMENT_STATUS } from '@/enums/paymentStatus'
import { RESULTS_PER_PAGE_OPTIONS } from '@/utils/resultsPerPageOptions'
import { RESERVATION_LIST_TYPE } from '@/enums/reservationListType'
import { SELLER_CATEGORY_TYPE } from '@/enums/sellerCategoryType'
import reservationService from '@/api/reservation/reservationService'

export default {
    props: {
        reservationMainFilters: {
            type: Object,
            default: () => ({
                dateRange: [null, null],
                selectedView: { id: RESERVATION_LIST_TYPE.DEPARTURE_DATE }
            })
        },
        pagination: {
            type: Object,
            default: () => ({ currentPage: 1 })
        }
    },
    data() {
        return {
            config: {},
            reservationsArr: [],
            isLoadingReservations: false,
            isOpeningReservation: false,
            pageInfo: null,
            filters: {
                searchString: null,
                productId: [],
                agentId: null,
                userId: null,
                supplierId: null,
                eventId: null,
                currentCheckboxNoCheckin: false,
                channelType: null,
                locations: []
            },
            exportOptions: [
                {
                    label: 'Excel',
                    action: this.getExcel
                }
            ],
            resultsPerPage: 20,
            resultsPerPageOptions: RESULTS_PER_PAGE_OPTIONS,
            bulkSelectAll: false,
            selectedReservationIds: []
        }
    },
    created() {
        // Clears the bulkSelectedIds array on the store
        this.resetReservationsFilters();
        this.setBulkSelectedIds([]);
        this.downloadFilterLocations();

        // Gets reservations of a given event (id)
        if (this.getSelectedEventListReservations) {
            this.filters.eventId = this.getSelectedEventListReservations.id;
        }

        this.getReservationsPaginated();

        this._onUpdate = () => {
            if (this.reservationMainFilters.selectedView.id !== this.config.viewId) {
                return;
            }

            this.getReservationsPaginated();
        };

        this._onUpdateRow = (data) => {
            if (this.reservationMainFilters.selectedView.id !== this.config.viewId) {
                return;
            }

            const index = this.reservationsArr.findIndex(r => r.id === data.id);
            if (index === -1) return;

            this.$set(this.reservationsArr, index, { ...this.reservationsArr[index], ...data });
        };

        this._onClear = () => {
            this.clearFilters();
        };

        this._onSearch = () => {
            this.getReservationsPaginated();

            // Clear reservations selection
            this.setBulkSelectedIds([]);
            this.selectAllReservations(false);

            this.resetPagination();
        };

        eventHub.$on('updateReservationsList', this._onUpdate);
        eventHub.$on('clearReservationFilters', this._onClear);
        eventHub.$on('filtersSearch', this._onSearch);
        this.$on('updateReservationsRow', this._onUpdateRow);
    },
    beforeDestroy() {
        eventHub.$off('updateReservationsList', this._onUpdate);
        eventHub.$off('clearReservationFilters', this._onClear);
        eventHub.$off('filtersSearch', this._onSearch);
        this.$off('updateReservationsRow', this._onUpdateRow);
    },
    computed: {
        ...mapGetters([
            'account',
            'getAgents',
            'getSlimFilters',
            'getSelectedEventListReservations',
            'getSelectedReservation',
            'getBulkSelectedIds',
            'getShowInvoiceOption',
            'getReservationState',
            'getPaymentState',
            'getEventColors',
            'getLocations',
            'getReservationsFilters',
            'getAllIntegrations',
            'getReservationCalculations'
        ]),
        bulkInvoiceTotal() {
            return this.getBulkSelectedIds.length
        },
        allowSelectRows() {
            return this.getShowInvoiceOption;
        },
        locations() {
            return this.getLocations.map((name, idx) => ({ id: idx + 1, name }))
        },
        treatedProductList() {
            return this.getSlimFilters.productList
        },
        hasSelectedReservationFilters() {
            const reservationFilters = this.getReservationsFilters;

            return (
                reservationFilters.resourceId.length > 0 ||
                reservationFilters.paymentStatus.length > 0 ||
                reservationFilters.countryId != null ||
                reservationFilters.status.length > 0 ||
                reservationFilters.currentCheckboxPickup ||
                reservationFilters.currentCheckboxNote ||
                reservationFilters.categories.length > 0
            );
        },
        agentMe() {
            return {
                brandName: `${this.account.name} (Me)`,
                name: null
            }
        },
        editedAgentList() {
            let agentList = JSON.parse(JSON.stringify(this.getAgents));

            let agentMustHaveName = agentList.filter(agent => {
                return agent.name != null;
            });

            agentMustHaveName.unshift(this.agentMe);
            agentMustHaveName.unshift({
                id: null,
                brandName: 'All Sellers'
            });

            return agentMustHaveName;
        },
        paginationComputed: {
            get() {
                return this.pagination;
            },
            set(newValue) {
                this.$emit('paginationChanged', newValue);
            }
        }
    },
    watch: {
        reservationMainFilters: {
            handler() {
                this.onFilterChange();
            },
            deep: true
        },
        filters: {
            handler() {
                this.onFilterChange();
            },
            deep: true
        },
        getSelectedEventListReservations: {
            handler() {
                if (this.getSelectedEventListReservations) {
                    this.filters.eventId = this.getSelectedEventListReservations.id;
                } else {
                    this.filters.eventId = null;
                }

                this.getReservationsPaginated();
            }
        },
    },
    methods: {
        ...mapActions([
            'setSelectedReservation',
            'openModal',
            'pushReservationToStore',
            'setBulkSelectedIds',
            'addToBulkSelectedIds',
            'removeFromBulkSelectedIds',
            'setReservationListFilters',
            'setReservationListAllSelected',
            'downloadFilterLocations',
            'resetReservationsFilters',
            'setReservationsType'
        ]),
        onPageChange(page) {
            if (this.pagination.currentPage === page) return;
            this.pagination.currentPage = page;
            this.getReservationsPaginated();
        },
        onFilterChange() {
            this.pagination.currentPage = 1;

            this.setBulkSelectedIds([]);
            this.selectAllReservations(false);

            this.getReservationsPaginated();
        },
        multiLevelHtml(top, bottom) {
            return (
                '<div><b>' + top + '</b></div>' +
                '<div class="sub">' + bottom + '</div>'
            )
        },
        openFilters(type) {
            this.setReservationsType(type);
            this.openModal('ReservationsFilters');
        },
        resultsPerPageChanged() {
            this.pagination.currentPage = 1;
            this.getReservationsPaginated();
            this.resetPagination();
        },
        clearFilters() {
            // Also clear the selection
            this.selectAllReservations(false);
            this.setBulkSelectedIds([]);
            this.resetReservationsFilters();

            this.filters = {
                productId: [],
                agentId: null,
                userId: null,
                supplierId: null,
                eventId: null,
                currentCheckboxNoCheckin: false,
                locations: []
            };

            // Clear multiSelects search
            ['productFilter', 'locationFilter', 'sellerSelector'].forEach(ref => {
                const target = this.$refs[ref];
                if (target && typeof target.search !== 'undefined') {
                    target.search = "";
                }
            });

            if (this.$refs.sellerSelector) {
                this.$refs.sellerSelector.deselectSeller();
            }

            this.onFilterChange();
            this.resetPagination();
        },
        resetPagination() {
            this.$emit('resetPagination');
        },
        selectCurrentPage(selectVal) {
            let currentPageSelectableIds = [];

            this.reservationsArr.forEach(row => {
                const isDisabled = row.paymentStatus !== 1;

                // Prevent disabled rows from being selected
                if (isDisabled) {
                    this.$set(row, 'selected', false);
                    return;
                }

                this.$set(row, 'selected', selectVal);
                currentPageSelectableIds.push(row.id);
            });

            this.$set(this, 'selectedReservationIds', currentPageSelectableIds);

            if (selectVal) {
                this.addToBulkSelectedIds(currentPageSelectableIds);
            } else {
                this.removeFromBulkSelectedIds(currentPageSelectableIds);
            }
        },
        selectAllReservations(state) {
            this.setReservationListAllSelected(state);

            for (let reservation of this.reservationsArr) {
                this.$set(reservation, 'selected', state);

                // If adding to selection
                if (state) {
                    if (this.selectedReservationIds.indexOf(reservation.id) == -1) {
                        this.selectedReservationIds.push(reservation.id);
                    }
                } else {
                    // If removing from selection
                    if (this.selectedReservationIds.indexOf(reservation.id) != -1) {
                        this.selectedReservationIds.splice(this.selectedReservationIds.indexOf(reservation.id), 1);
                    }
                }
            }
        },
        openOptions() {
            this.setReservationListFilters(this.requestFilterOptions);
            this.openModal('ReservationsBulkActions');
        },
        selectSelectedReservations(reservations) {
            let newBulkSelectAll = reservations.length > 0;

            for (let reservation of reservations) {
                if (this.getBulkSelectedIds.includes(reservation.id)) {
                    this.$set(reservation, 'selected', true);
                } else {
                    newBulkSelectAll = false;
                }
            }
            this.bulkSelectAll = newBulkSelectAll;

            return reservations;
        },
        sellerChanged(e) {
            this.resetPagination();

            const isSellerCategorySelected = e.id in SELLER_CATEGORY_TYPE;

            this.$set(this.filters, 'sellerType', isSellerCategorySelected ? SELLER_CATEGORY_TYPE[e.id] : null);
            this.$set(this.filters, 'channelType', isSellerCategorySelected ? null : e.type);
            this.$set(this.filters, 'sellerId', isSellerCategorySelected ? null : e.id);

            this.onFilterChange();
        },
        deselectSeller() {
            this.filters.sellerType = null;
            this.filters.channelType = null;
            this.filters.sellerId = null;
        },
        view(ticketNr) {
            // If the user alredy requested a reservation return
            if (this.isLoadingReservationData) return;
            this.isLoadingReservationData = true;

            axios.get('api/reservation/FindReservationBySupplier', {
                params: {
                    supplierId: this.account.supplierId,
                    reservationId: ticketNr
                }
            }).then((response) => {
                if (response !== null && response !== undefined && response.status == 200) {
                    if (response.data !== null) {
                        this.pushReservationToStore(response.data)
                        this.triggerModal(response.data, 'ReservationEditV2');
                    }
                }
                // Set back the isFetching to false so that the user can fetch more reservations
                this.isLoadingReservationData = false;
            }).catch((err) => {
                // Set back the isFetching to false so that the user can fetch more reservations
                this.isLoadingReservationData = false;
                throw err;
            });
        },
        async triggerModal(reservation, modal) {
            this.setSelectedReservation({ payload: reservation, isToResetFlags: true });
            this.openModal(modal);
        },
        async getExcel() {
            const result = await reservationService.exportReservations(this.config.downloadUrl, this.requestFilterOptions);
            if (!result.ok) return;

            window.URL = window.URL || window.webkitURL;
            const link = document.createElement('a');
            document.body.appendChild(link);
            link.href = window.URL.createObjectURL(result.value.blob);
            link.download = result.value.filename;
            link.click();
            URL.revokeObjectURL(link.href);
            document.body.removeChild(link);
        },
        cellProductColorSign(reservation) {
            let productColorSign = 'background-color: #fff; border: 1px solid #8ca0b3; color: #222c3c !important;';

            if (reservation.event.product !== undefined && reservation.event.product.color !== undefined) {
                let color = this.getEventColors.filter((storedColor) => {
                    return reservation.event.product.color == storedColor.id;
                })

                if (color && color.length > 0) {
                    productColorSign = 'background-color: ' + color[0].code + ';' + 'border: 1px solid' + color[0].codeSecundary + '; ' + 'color: #222c3c !important;';
                }
            }

            return '<div style="width: 15px; height: 15px; border-radius: 100px; background-color: #FFF; border-color: #8ca0b3; border: 1px solid; display: block; ' + productColorSign + ' "></div>';
        },
        cellReservation(reservation) {
            if (reservation.event.product) {
                const p = reservation.event.product;

                return this.multiLevelHtml(this.formatTicketNumber(reservation.id), p.name);
            } else {
                return 'missing';
            }
        },
        cellResource(reservation) {
            return reservation.selectedResource ? reservation.selectedResource.name : '';
        },
        cellDeparture(reservation) {
            return this.formatDateDots(reservation.eventDate) + '<br>' + this.formatTime(reservation.eventDate);
        },
        cellCreated(reservation) {
            return this.formatDateDots(reservation.reservationDate) + '<br>' + this.formatTime(reservation.reservationDate);
        },
        cellPax(reservation) {
            const persons = (reservation && reservation.passengerDetails) || [];

            const rows = persons
                .filter(p => Number(p.label) > 0)
                .map(p => {
                    const hasProps = Array.isArray(p.properties) && p.properties.length > 0;

                    const propsHtml = hasProps
                        ? p.properties.map(prop => `
                            <div class="d-flex justify-content-between fs-smaller">
                                <div class="dark">${prop.id}</div>
                                <div class="ml-2">${prop.label}</div>
                            </div>
                            `).join('')
                        : '';

                    return `
                        <div class="mb-2">
                            <div class="d-flex justify-content-between bold">
                                <div>${p.id}</div>
                                <div class="ml-2">${p.label}</div>
                            </div>
                            ${propsHtml}
                        </div>
                    `;
                })
                .join('');

            const hasTooltip = rows.length > 0;
            const detailsHtml = hasTooltip ? `<div class="d-flex flex-column">${rows}</div>` : '';

            return {
                html: `<span class="pax-value">${reservation.paxTotal}</span>`,
                tooltip: { content: detailsHtml, html: hasTooltip },
            };
        },
        cellTotalPrice(reservation) {
            return reservation.billingTotal ? this.formatPrice(reservation.billingTotal) + '€' : '-';
        },
        cellStatusReservation(reservation) {
            if (reservation.loading) {
                return '<div class="loaderHolder"><div class="loader">Loading...</div></div>';
            } else {
                let output = `<div class="">
                    <div class="lightest fs-smallest px-2 py-1 text-uppercase ${this.getReservationState(reservation.status).cssClass} radius-stadium text-center font-weight-600">
                        ${this.getReservationState(reservation.status).label}
                    </div>`;

                // Dont show if: billingInDue is 0 | reservation is canceled (-5) and the payment is 'In Due'
                if (reservation.billingInDue != 0 && !(reservation.status == RESERVATION_STATUS.CANCELED && reservation.paymentStatus == 0)) {
                    // This creates scpacing to aling if value exists in the next column
                    output += `<div>&nbsp;</div>`;
                }

                output += `</div>`;

                return output;
            }
        },
        cellStatusPayment(reservation) {
            if (reservation.loading) {
                return '<div class="loaderHolder"><div class="loader">Loading...</div></div>';
            } else {
                let output = `<div class="">
                    <div class="lightest fs-smallest px-2 py-1 radius-stadium text-center text-uppercase font-weight-600 ${this.getPaymentState(reservation.paymentStatus).cssClass}">
                        ${this.getPaymentState(reservation.paymentStatus).label}
                    </div>`;

                // Dont show if: billingInDue is 0 | reservation is canceled (-5) and the payment is 'In Due'
                if (reservation.billingInDue != 0 && !(reservation.status == RESERVATION_STATUS.CANCELED && reservation.paymentStatus == 0)) {
                    output += `<div class="pt-1 text-center fs-smaller">
                        ${this.formatPrice(reservation.billingInDue)}€
                    </div>`;
                }

                output += `</div>`;

                return output;
            }
        },
        cellFullName(reservation) {
            const c = reservation.customer;

            return (c.name !== undefined && c.surname !== undefined) ? c.name + ' ' + c.surname : '-';
        },
        cellSeller(reservation) {
            return reservation.createdByName;
        },
        cellExtras(reservation) {
            if (reservation.bookingComponents.length > 0) {
                // Group componets
                let existingGroup = JSON.parse(JSON.stringify(reservation.bookingComponentsGrouped));
                let groupedComponents = [];

                existingGroup.map(existingItem => {
                    let itemIsGrouped = groupedComponents.find(groupedItem => {
                        return groupedItem.component.id == existingItem.component.id;
                    });

                    if (!itemIsGrouped) {
                        groupedComponents.push(existingItem);
                    } else {
                        itemIsGrouped.quantity += existingItem.quantity;
                    }
                });

                let output = '';

                for (let extra of groupedComponents) {
                    if (extra.quantity > 0) {
                        output += '<div>' + extra.quantity + ' - ' + ((extra.component && extra.component.name) ? extra.component.name : '' + ' ' + (extra.componentPrice && extra.componentPrice.name) ? extra.componentPrice.name : '') + '</div>';
                    }
                }

                return output;
            } else {
                return '';
            }
        },
        cellCheckin(reservation) {
            return { component: CheckinDropdown, arguments: { reservation, getCheckinInfo: this.getCheckinInfo(reservation) },
                on: {
                    updateReservationsList: (data) => {
                        if (data && data.id) {
                            this.$emit('updateReservationsRow', data);
                            return;
                        }
                    }
                }
            };
        },
        cellIcons(reservation) {
            return {
                component: iconsColumn,
                arguments: reservation
            }
        },
        getCheckinInfo(reservation) {
            const checkedQuantity = Number(reservation.paxCheckedIn || 0);
            const noShowQuantity = Number(reservation.paxNoShow || 0);

            let totalPax = reservation.paxTotal;

            const isCancelled = reservation.status === RESERVATION_STATUS.CANCELED || reservation.status === 'Canceled';
            const isPendingOrDue = reservation.status === RESERVATION_STATUS.PENDING || reservation.paymentStatus === PAYMENT_STATUS.IN_DUE;
            const isDisabled = isCancelled || isPendingOrDue || totalPax === 0;

            let display = '';
            if (isCancelled || isPendingOrDue) display = '- | -';
            else if (checkedQuantity === 0 && noShowQuantity === 0) display = 'Not checked in';
            else display = `${checkedQuantity} | ${noShowQuantity}`;

            return { checkedQuantity, noShowQuantity, totalPax, isCancelled, isDisabled, display };
        },
        cellPickupDate(reservation) {
            if (reservation.transport.transfers.length > 0) {
                return this.formatDateDots(reservation.transport.transfers[0].date) + '<br>' + this.formatTime(reservation.transport.transfers[0].date);
            } else {
                return '-';
            }
        },
        cellPickupLocation(reservation) {
            if (reservation.transport.transfers.length > 0) {
                return reservation.transport.transfers[0].stop.name;
            } else {
                return '-';
            }
        },
        cellHotel(reservation) {
            if (reservation.customer.room != "") {
                return reservation.customer.accomodation + ' / ' + reservation.customer.room;
            } else {
                return reservation.customer.accomodation;
            }
        },
    }
}



// WEBPACK FOOTER //
// ./src/mixins/reservationsLogicMixin.js